import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppStore } from '../../../../../redux/hooks';
import { addAppliedJob, updateJobApplicationState, updateJobHiddenState, type ApplicationState } from '../../../../../redux/features/jobs/jobsSlice';
import { useSavedJobActions } from '../../../../../hooks/useSavedJobActions';
import { useUserJobStateSync } from '../../../../../hooks/useUserJobStateSync';
import { trackJobApply, trackJobSave, trackJobUnsave } from '../../../../../utils/analytics/analytics';
import { APPLICATION_STATES } from '../../../../../components/application-state';
import type { DisplayJob } from '../../../../../types';

export interface JobActions {
  handleToggleSaved: () => Promise<void>;
  handleToggleApplied: () => Promise<void>;
  handleToggleHide: () => void;
  handleSetApplicationState: (state: ApplicationState) => Promise<void>;
  handleShare: () => Promise<void>;
}

export interface UseJobActionsProps {
  displayJob: DisplayJob | null | undefined;
  jobId: string | undefined;
  isSaved: boolean;
  isApplied: boolean;
  isHidden: boolean;
  showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number) => void;
  url?: string;
}

/**
 * Hook to manage all actions for a job detail page
 * Handles save, unsave, apply, hide, share, and application state changes
 */
export function useJobActions({
  displayJob,
  jobId,
  isSaved,
  isApplied,
  isHidden,
  showToast,
  url,
}: UseJobActionsProps): JobActions {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const { saveJob, unsaveJob } = useSavedJobActions();
  const { setAppliedJobState, syncApplicationStateToBackend, syncJobIsHiddenToBackend } =
    useUserJobStateSync();

  const handleToggleSaved = useCallback(async () => {
    if (!displayJob) return;
    const payload = { ...displayJob, id: displayJob.objectID };
    
    if (!isSaved) {
      trackJobSave({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        locale: intl.locale,
      });
      const success = await saveJob(payload);
      if (success) {
        showToast(
          intl.formatMessage({
            id: 'hit.toast.saved',
            defaultMessage: 'Job saved',
            description: 'Success toast notification message shown when a job is saved',
          }),
          'success'
        );
      }
    } else {
      trackJobUnsave({
        jobId: displayJob.objectID,
        jobTitle: displayJob.normalisedTitle || displayJob.title || '',
        locale: intl.locale,
      });
      const success = await unsaveJob(payload);
      if (success) {
        showToast(
          intl.formatMessage({
            id: 'hit.toast.unsaved',
            defaultMessage: 'Job removed from saved',
            description: 'Info toast notification message shown when a job is removed from saved jobs',
          }),
          'info'
        );
      }
    }
  }, [displayJob, isSaved, saveJob, unsaveJob, intl, showToast]);

  const handleToggleApplied = useCallback(async () => {
    if (!displayJob) return;
    const willBeApplied = !isApplied;
    const payload = { ...displayJob, id: displayJob.objectID };
    const success = await setAppliedJobState(payload, willBeApplied);
    if (success) {
      if (willBeApplied) {
        trackJobApply({
          jobId: displayJob.objectID,
          jobTitle: displayJob.normalisedTitle || displayJob.title || '',
          companyId: displayJob.companyId || '',
          locale: intl.locale,
        });
        showToast(
          intl.formatMessage({
            id: 'hit.toast.applied',
            defaultMessage: 'Marked as applied',
            description: 'Success toast notification message shown when a job is marked as applied to',
          }),
          'success'
        );
      } else {
        showToast(
          intl.formatMessage({
            id: 'hit.toast.unapplied',
            defaultMessage: 'Removed from applied',
            description: 'Info toast notification message shown when applied status is removed from a job',
          }),
          'info'
        );
      }
    }
  }, [isApplied, setAppliedJobState, displayJob, intl, showToast]);

  const handleToggleHide = useCallback(() => {
    if (!jobId) return;
    const willBeHidden = !isHidden;
    dispatch(updateJobHiddenState({ jobId, isHidden: willBeHidden }));
    void syncJobIsHiddenToBackend(jobId);

    setTimeout(() => {
      const state = store.getState();
      const allJobs = [...state.jobs.savedJobs, ...state.jobs.viewedJobs, ...state.jobs.appliedJobs];
      const job = allJobs.find((j) => j.id === jobId);
      const hasCorrectState = job?.isHidden === willBeHidden;

      if (hasCorrectState) {
        showToast(
          willBeHidden
            ? intl.formatMessage({
                id: 'hit.toast.hidden',
                defaultMessage: 'Job hidden',
                description: 'Info toast notification message shown when a job is hidden',
              })
            : intl.formatMessage({
                id: 'hit.toast.unhidden',
                defaultMessage: 'Job unhidden',
                description: 'Success toast notification message shown when a previously hidden job is unhidden',
              }),
          willBeHidden ? 'info' : 'success'
        );
      }
    }, 0);
  }, [isHidden, dispatch, jobId, intl, showToast, store, syncJobIsHiddenToBackend]);

  const handleSetApplicationState = useCallback(async (state: ApplicationState) => {
    if (!displayJob || !jobId) return;

    const payload = { ...displayJob, id: displayJob.objectID };
    const inApplied = store.getState().jobs.appliedJobs.some((j) => j.id === jobId);
    if (!inApplied) {
      dispatch(
        addAppliedJob({
          ...payload,
          applicationState: state,
          appliedAt: new Date().toISOString(),
        } as Parameters<typeof addAppliedJob>[0])
      );
    } else {
      dispatch(updateJobApplicationState({ jobId, applicationState: state }));
    }

    const success = await syncApplicationStateToBackend(jobId, state, payload);

    if (success) {
      if (state !== null && !isApplied) {
        trackJobApply({
          jobId: displayJob.objectID,
          jobTitle: displayJob.normalisedTitle || displayJob.title || '',
          companyId: displayJob.companyId || '',
          locale: intl.locale,
        });
      }

      const stateConfig = APPLICATION_STATES.find((s) => s.value === state);
      if (stateConfig) {
        showToast(
          intl.formatMessage(
            {
              id: 'tools.applicationState.updated',
              defaultMessage: 'State updated to {state}',
              description: 'Success toast message when application state is changed, where {state} is the new state name',
            },
            { state: intl.formatMessage({ id: stateConfig.labelKey, defaultMessage: stateConfig.defaultLabel }) }
          ),
          'success'
        );
      }
    }
  }, [dispatch, store, jobId, intl, showToast, isApplied, syncApplicationStateToBackend, displayJob]);

  const handleShare = useCallback(async () => {
    if (!displayJob) return;
    const { sharePage } = await import('../utils/shareUtils');
    const currentUrl = typeof window !== 'undefined' ? window.location.href : url || '';
    const jobTitle = displayJob.title || displayJob.normalisedTitle || document.title;

    await sharePage(
      currentUrl,
      jobTitle,
      () => {
        showToast(
          intl.formatMessage({
            id: 'hit.linkCopied',
            defaultMessage: 'Link copied to clipboard!',
            description: 'Message shown when link is copied to clipboard',
          }),
          'success'
        );
      },
      (error) => {
        console.error('Share failed:', error);
        showToast(
          intl.formatMessage({
            id: 'hit.shareError',
            defaultMessage: 'Something went wrong while trying to share this job.',
            description: "Message shown when there's an error sharing",
          }),
          'error'
        );
      }
    );
  }, [displayJob, url, intl, showToast]);

  return {
    handleToggleSaved,
    handleToggleApplied,
    handleToggleHide,
    handleSetApplicationState,
    handleShare,
  };
}
